<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import FormInput from '../../components/Form/Input.vue';
import SubmitButton from '../../components/Button/SubmitButton.vue';
import { DEFAULT_REDIRECT_URL } from 'dashboard/constants/globals';
import { setNewPassword } from 'v3/api/auth';
import Slogan from '../SloganBox.vue';
import GapifyHeader from '../AuthHeader.vue';
import GapifySignBox from '../AuthFormBox.vue';
import CheckBox from '../../components/Form/CheckBox.vue';
import RowCheckBox from '../RowCheckBox.vue';
import GapifyRulesConfirmation from '../RulesConfirmation.vue';
import { BaseButton } from '../../components/Button';
import AuthLayout from '../AuthLayout.vue';
import OnboardingMotionLogin from '../login/OnboardingMotionLogin.vue';

export default {
  components: {
    OnboardingMotionLogin,
    AuthLayout,
    BaseButton,
    GapifyRulesConfirmation,
    RowCheckBox,
    CheckBox,
    GapifySignBox,
    GapifyHeader,
    FormInput,
    SubmitButton,
    Slogan,
  },
  props: {
    resetPasswordToken: { type: String, default: '' },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        confirmPassword: '',
        password: '',
      },
      newPasswordAPI: {
        message: '',
        showLoading: false,
      },
      checkedRules: false,
      error: '',
    };
  },
  mounted() {
    // If url opened without token
    // redirect to login
    if (!this.resetPasswordToken) {
      window.location = DEFAULT_REDIRECT_URL;
    }
  },
  validations: {
    credentials: {
      password: {
        required,
        minLength: minLength(6),
      },
      confirmPassword: {
        required,
        minLength: minLength(6),
        isEqPassword(value) {
          if (value !== this.credentials.password) {
            return false;
          }
          return true;
        },
      },
    },
  },
  methods: {
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.newPasswordAPI.showLoading = false;
      useAlert(message);
    },
    submitForm() {
      this.newPasswordAPI.showLoading = true;
      const credentials = {
        confirmPassword: this.credentials.confirmPassword,
        password: this.credentials.password,
        resetPasswordToken: this.resetPasswordToken,
      };
      setNewPassword(credentials)
        .then(() => {
          window.location = DEFAULT_REDIRECT_URL;
        })
        .catch(error => {
          this.showAlertMessage(
            error?.message || this.$t('SET_NEW_PASSWORD.API.ERROR_MESSAGE')
          );
        });
    },
  },
};
</script>

<template>
  <AuthLayout>
    <template #right>
      <OnboardingMotionLogin />
    </template>
    <template #left>
      <div class="w-full px-8 py-10 dark:bg-slate-900">
        <section class="max-w-5xl mx-auto">
          <GapifyHeader
            :show-description="false"
            :title-text="$t('SET_NEW_PASSWORD.TITLE')"
          />
          <form class="mt-2" @submit.prevent="submitForm">
            <div class="space-y-8">
              <FormInput
                v-model="credentials.password"
                name="password"
                type="password"
                :label="$t('SET_NEW_PASSWORD.PASSWORD.LABEL')"
                :has-error="v$.credentials.password.$error"
                :error-message="$t('SET_NEW_PASSWORD.PASSWORD.ERROR')"
                :placeholder="$t('SET_NEW_PASSWORD.PASSWORD.PLACEHOLDER')"
                is-first-item
                @blur="v$.credentials.password.$touch"
              />
              <FormInput
                v-model="credentials.confirmPassword"
                name="confirm_password"
                type="password"
                :label="$t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.LABEL')"
                :has-error="v$.credentials.confirmPassword.$error"
                :error-message="$t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.ERROR')"
                :placeholder="
                  $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.PLACEHOLDER')
                "
                @blur="v$.credentials.confirmPassword.$touch"
              />
              <GapifyRulesConfirmation v-model:checked-rules="checkedRules" />
              <div class="flex w-full justify-end">
                <SubmitButton
                  :disabled="
                  v$.credentials.password.$invalid ||
                  v$.credentials.confirmPassword.$invalid ||
                  newPasswordAPI.showLoading ||
                  !checkedRules
                "
                  :button-text="$t('SET_NEW_PASSWORD.SUBMIT')"
                  :loading="newPasswordAPI.showLoading"
                  class="min-[1440px]:!w-20"
                />
              </div>
            </div>
          </form>
        </section>
      </div>
    </template>
  </AuthLayout>
</template>
